import React, { createContext, useContext, useState, useCallback } from 'react';

const translations = {
  en: {
    nav: { mission: 'Mission', workflow: 'How We Work', services: 'Services', faq: 'FAQ', contact: 'Contact' },
    hero: {
      headlineL1: 'We create unforgettable',
      headlineRed: '',
      headlineL2: 'brands',
      subheadline: 'We design your identity to provide a unique experience for your customer.',
      cta: 'Start a Project'
    },
    mission: {
      label: 'Our Mission',
      text: 'In a world obsessed with the ephemeral, we build brands that endure. We design identities that stand out from the rest of the market and bring value to your business.'
    },
    workflow: {
      label: 'Workflow',
      cta: 'Let\'s start my project',
      steps: [
        { number: '01', title: 'Discover', desc: 'We immerse ourselves in your brand, understanding your audience, their needs, and your goals. Every project starts from comprehension, not assumptions.' },
        { number: '02', title: 'Design', desc: 'We create prototypes and visual systems aligned with the strategy, positioning you in the minds of your target audience and setting you apart from the competition.' },
        { number: '03', title: 'Deliver', desc: 'We build, test, and launch with precision. The result is a product that performs as good as it looks.' }
      ]
    },
    services: {
      label: 'Services',
      items: [
        { title: 'Web Design', desc: 'We create your custom website with meticulous attention to detail and a smooth, seamless user experience. From landing pages to complex websites.' },
        { title: 'Content Creation', desc: 'Strategic content that elevates your identity. Photography, video, and copywriting aligned with your business values.' },
        { title: 'Branding', desc: 'We fully design your brand identity. From logos, typography, colors, and voice, we enhance everything that makes you recognizable.' }
      ]
    },
    faq: {
      label: 'FAQ',
      items: [
        { q: 'How long does a project take?', a: 'It depends on each project\'s goal. Most projects take 4 to 8 weeks, from first contact to completion.' },
        { q: 'What types of businesses do you work with?', a: 'We love working with any type of business, whether new or an established brand. What we value is ambition and a clear vision of where you want to go.' },
        { q: 'What about pricing?', a: 'Every project is unique. After an initial call to understand the business needs, we provide a detailed proposal with transparent pricing.' },
        { q: 'Do you offer ongoing maintenance?', a: 'Generally yes. We offer monthly maintenance and support services for those projects that need it.' },
        { q: 'What services do you cover exactly?', a: 'Beyond what\'s already mentioned, we cover everything it entails — from personalized consulting to understand your business, through graphic design, content strategy creation, social media management, to SEO to position your brand. All under one roof, so your message is coherent across every touchpoint of marketing.' }
      ]
    },
    contact: {
      label: 'Let\'s Talk',
      subtitle: 'Let\'s work together on something extraordinary.',
      name: 'Full name',
      email: 'Email address',
      message: 'Tell us about your business',
      send: 'Let\'s start my project',
      sending: 'Sending...',
      success: 'Message sent! We\'ll be in touch soon.',
      error: 'Something went wrong. Please try again.',
      namePlaceholder: 'Your full name',
      emailPlaceholder: 'you@email.com',
      messagePlaceholder: 'Tell us about your project...'
    },
    footer: {
      rights: 'All rights reserved.'
    }
  },
  es: {
    nav: { mission: 'Misión', workflow: 'Proceso', services: 'Servicios', faq: 'FAQ', contact: 'Contacto' },
    hero: {
      headlineL1: 'Creamos marcas',
      headlineRed: '',
      headlineL2: 'que no se olvidan',
      subheadline: 'Diseñamos tu identidad aportando una experiencia única a tu cliente.',
      cta: 'Comencemos'
    },
    mission: {
      label: 'Nuestra Misión',
      text: 'En un mundo obsesionado con lo efímero, construimos marcas que perduran. Diseñamos identidades que se diferencian del resto del mercado y aportan valor a tu negocio.'
    },
    workflow: {
      label: 'Fases',
      cta: 'Comencemos con mi proyecto',
      steps: [
        { number: '01', title: 'Descubrir', desc: 'Nos sumergimos en tu marca, entendiendo a tu audiencia, sus necesidades y tus objetivos. Cada proyecto parte de la comprensión, no de suposiciones.' },
        { number: '02', title: 'Diseñar', desc: 'Creamos prototipos y sistemas visuales alineados con la estrategia, posicionándote en la mente del público objetivo y diferenciándote de la competencia.' },
        { number: '03', title: 'Desarrollar', desc: 'Construimos, probamos y lanzamos con precisión. El resultado es un producto que rinde tan bien como se ve.' }
      ]
    },
    services: {
      label: 'Servicios',
      items: [
        { title: 'Diseño Web', desc: 'Creamos tu sitio web personalizado con todo tipo de detalles y una experiencia de usuario suave y fluida. Desde landing pages hasta páginas webs complejas.' },
        { title: 'Creación de Contenido', desc: 'Contenido estratégico que realza tu identidad. Fotografía, video y copywriting acorde a los valores de tu negocio.' },
        { title: 'Branding', desc: 'Diseñamos por completo la identidad de tu marca. Desde logos, tipografías, colores y su voz, potenciamos todo aquello que te hace reconocible.' }
      ]
    },
    faq: {
      label: 'Preguntas Frecuentes',
      items: [
        { q: '¿Cuál es la duración de un proyecto?', a: 'Depende del objetivo de cada uno. La mayoría de los proyectos toman de 4 a 8 semanas, desde el primer contacto hasta la finalización del mismo.' },
        { q: '¿Con qué tipos de negocios trabajan?', a: 'Nos encanta trabajar con cualquier tipo de negocio, ya sea nuevo o una marca establecida. Lo que valoramos es la ambición y una visión clara de hacia dónde se quiere ir.' },
        { q: '¿Cómo son los precios?', a: 'Cada proyecto es único. Después de una primera llamada para conocer las necesidades del negocio, proporcionamos una propuesta detallada con precios transparentes.' },
        { q: '¿Ofrecen mantenimiento continuo?', a: 'Por lo general sí. Ofrecemos servicios mensuales de mantenimiento y soporte en aquellos proyectos que lo necesitan.' },
        { q: '¿Qué servicios abarcan exactamente?', a: 'Además de lo ya mencionado, abarcamos todo lo que ello conlleva; desde una consultoría personalizada para entender tu negocio, pasando por diseño gráfico, la creación de estrategia de contenidos, gestión de redes sociales, hasta el SEO para posicionar a tu marca. Todo ello bajo un mismo techo, para que tu mensaje sea coherente desde todos los puntos del marketing.' }
      ]
    },
    contact: {
      label: 'Hablemos',
      subtitle: 'Trabajemos juntos en algo extraordinario.',
      name: 'Nombre y apellidos',
      email: 'Correo electrónico',
      message: 'Coméntanos acerca de tu negocio',
      send: 'Comencemos con mi proyecto',
      sending: 'Enviando...',
      success: '¡Mensaje enviado! Te contactaremos pronto.',
      error: 'Algo salió mal. Inténtalo de nuevo.',
      namePlaceholder: 'Tu nombre y apellidos',
      emailPlaceholder: 'tu@email.com',
      messagePlaceholder: 'Cuéntanos sobre tu proyecto...'
    },
    footer: {
      rights: 'Todos los derechos reservados.',
      legalNotice: 'Aviso Legal',
      privacyPolicy: 'Política de Privacidad',
      cookiePolicy: 'Política de Cookies'
    }
  }
};

const LanguageContext = createContext();

export function LanguageProvider({ children }) {
  const [lang, setLang] = useState('es');
  const toggleLang = useCallback(() => setLang(l => l === 'es' ? 'en' : 'es'), []);
  const t = translations[lang];
  return (
    <LanguageContext.Provider value={{ lang, toggleLang, t }}>
      {children}
    </LanguageContext.Provider>
  );
}

export function useLanguage() {
  return useContext(LanguageContext);
}
